/* Photo product bridge v1. Commerce stays on Shopify; the customer studio never
 * chooses a variant ID or a price. The existing expert produces the atelier files.
 * Message contract: {channel:'mt-photo-product', type, session, requestId, ...}.
 */
(() => {
  'use strict';
  const CHANNEL = 'mt-photo-product';
  const STUDIO_ORIGIN = 'https://admin.mytomette.fr';
  const MAX_RECIPE_BYTES = 1200000;
  const encoder = new TextEncoder();
  const bytes = value => encoder.encode(value).length;
  const uuid = () => crypto.randomUUID();
  const validId = value => typeof value === 'string' && /^[a-zA-Z0-9_-]{8,100}$/.test(value);
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  function mount(root) {
    if (root.dataset.photoMounted) return;
    root.dataset.photoMounted = 'true';
    const config = JSON.parse(root.querySelector('[data-photo-config]').textContent);
    const studio = root;
    const status = root.querySelector('[data-photo-status]');
    const price = root.querySelector('[data-photo-price]');
    const dialog = root.querySelector('[data-photo-expert]');
    const expertHost = root.querySelector('[data-photo-expert-host]');
    const expertAdd = root.querySelector('[data-photo-expert-add]');
    const expertStatus = root.querySelector('[data-photo-expert-status]');
    const english = /^en\b/i.test(config.locale);
    const spanish = !english && /^es\b/i.test(config.locale || '');
    const SAY_ES = spanish ? {
      "Ce format est momentanément indisponible.": "Este formato no está disponible por ahora.",
      "Le dessin contient un élément non compatible avec la gravure.": "El dibujo contiene un elemento que no se puede grabar.",
      "Le dessin est vide.": "El dibujo está vacío.",
      "Le dessin est trop volumineux pour être transmis.": "El dibujo es demasiado pesado para enviarlo.",
      "La gravure de votre photo est manquante.": "Falta el grabado de tu foto.",
      "Cet ajout ne correspond pas à cette version de votre dessin. Vérifiez à nouveau votre création.": "Este artículo no corresponde a esta versión de tu dibujo. Revisa de nuevo tu creación.",
      "Cet ajout est incomplet. Contactez-nous avant de valider votre commande.": "Este artículo está incompleto. Contacta con nosotros antes de confirmar tu pedido.",
      "Cette création n’est pas encore disponible à l’achat. Votre dessin est conservé ; réessayez un peu plus tard.": "Esta creación aún no está disponible para la compra. Tu dibujo se conserva; inténtalo de nuevo un poco más tarde.",
      "Ce format n’est pas disponible à l’achat pour le moment. Votre dessin est conservé ; choisissez un autre format ou réessayez plus tard.": "Este formato no está disponible para la compra por ahora. Tu dibujo se conserva; elige otro formato o inténtalo de nuevo más tarde.",
      "Configurateur expert de votre photo": "Editor experto de tu foto",
      "Le moteur de gravure ne répond pas. Votre dessin est conservé ; réessayez.": "El motor de grabado no responde. Tu dibujo se conserva; inténtalo de nuevo.",
      "La préparation du fichier prend trop de temps. Réessayez sans recréer votre dessin.": "La preparación del archivo está tardando demasiado. Inténtalo de nuevo sin volver a crear tu dibujo.",
      "Une police ou un motif n’a pas pu être préparé. Aucun article ajouté.": "No se ha podido preparar una fuente o un motivo. No se ha añadido ningún artículo.",
      "L’aperçu n’a pas pu être préparé.": "No se ha podido preparar la vista previa.",
      "Votre création n’a pas pu être chargée.": "No se ha podido cargar tu creación.",
      "La nouvelle version est au panier, mais l’ancienne n’a pas pu être retirée. Réessayez pour terminer.": "La nueva versión está en la cesta, pero no se ha podido quitar la anterior. Inténtalo de nuevo para terminar.",
      "Votre dessin a changé. Validez à nouveau cette version.": "Tu dibujo ha cambiado. Confirma de nuevo esta versión.",
      "La réponse du panier tarde. Patientez un instant avant de vérifier à nouveau cet ajout.": "La cesta tarda en responder. Espera un momento antes de volver a comprobar este artículo.",
      "Préparation de votre aperçu et de la gravure…": "Preparando tu vista previa y el grabado…",
      "Sans texte": "Sin texto",
      " · avec trou": " · con agujero",
      " · sans trou": " · sin agujero",
      "Ajout de votre création au panier…": "Añadiendo tu creación a la cesta…",
      "Vérification du panier nécessaire. Réessayez pour vérifier cet ajout, sans recréer votre dessin.": "Hay que comprobar la cesta. Inténtalo de nuevo para comprobar este artículo, sin volver a crear tu dibujo.",
      "L’ajout au panier n’est pas encore confirmé. Votre dessin est conservé. Réessayez pour vérifier ou terminer cet ajout.": "La cesta aún no ha confirmado el artículo. Tu dibujo se conserva. Inténtalo de nuevo para comprobarlo o terminar de añadirlo.",
      "Votre nouvelle version est au panier. Réessayez pour terminer le remplacement de l’ancienne version.": "Tu nueva versión está en la cesta. Inténtalo de nuevo para terminar de sustituir la versión anterior.",
      "Votre création est en cours d’ajout. Patientez un instant.": "Tu creación se está añadiendo. Espera un momento.",
      "Cette modification n’a pas pu être enregistrée. Votre dernière version valide reste disponible dans le configurateur expert.": "No se ha podido guardar este cambio. Tu última versión válida sigue disponible en el editor experto.",
      "Votre version expert est conservée sur cette page. Retrouvez-la dans le configurateur expert pour l’ajouter au panier.": "Tu versión del editor experto se conserva en esta página. Vuelve a abrir el editor experto para añadirla a la cesta.",
      "Attendez la fin de l’ajout au panier.": "Espera a que termine de añadirse a la cesta.",
      "L’éditeur n’a pas pu afficher votre création. Votre dessin est conservé ; réessayez.": "El editor no ha podido mostrar tu creación. Tu dibujo se conserva; inténtalo de nuevo.",
      "L’éditeur ne répond pas. Quitter sans enregistrer ? Votre création risque d’être perdue.": "El editor no responde. ¿Salir sin guardar? Podrías perder tu creación.",
      "Cette création photo ne peut pas être rouverte. Votre lien est conservé ; contactez-nous pour la récupérer.": "Esta creación con foto no se puede volver a abrir. Tu enlace se conserva; contacta con nosotros para recuperarla.",
      "Cette création n’est plus dans votre panier.": "Esta creación ya no está en tu cesta.",
      "Cette création sera bientôt disponible.": "Esta creación estará disponible pronto.",
      "Votre création est dans le panier.": "Tu creación está en la cesta.",
      "Le studio prend un peu de temps à charger. Votre panier reste disponible.": "El estudio tarda un poco en cargar. Tu cesta sigue disponible.",
    } : null;
    const say = (fr, en) => english ? en : spanish ? (SAY_ES[fr] ?? fr) : fr;
    const session = uuid();
    const variants = new Map();
    let ready = false, renderer = null, rendererTask = null, active = null;
    let expertRequest = null, expertRetryId = null, expertRetryDigest = null, edit = null, restored = false;
    let expertBaseFingerprint = null;
    // Expert edits belong to one exact guided composition. Keep them on this
    // page without modifying the guided form or its saved original drawing.
    // A change of photo, text, shape or framing creates a different key.
    const expertDrafts = new Map();
    // Keep the expert in one browsing context for its entire lifetime. Moving
    // an iframe between parents reloads it and silently drops its recipe.
    // A closed <dialog> normally has display:none; park it outside the viewport
    // with a real layout so background font/canvas exports remain deterministic.
    const parkedStyle = document.createElement('style');
    parkedStyle.textContent = '[data-photo-product] [data-photo-expert]:not([open]){display:flex!important;flex-direction:column;position:fixed!important;inset:auto!important;left:-10000px!important;top:0!important;width:1200px!important;height:900px!important;max-width:none!important;max-height:none!important;margin:0!important;visibility:hidden!important;pointer-events:none!important}';
    root.append(parkedStyle);
    dialog.setAttribute('inert', ''); dialog.setAttribute('aria-hidden', 'true');
    const complete = new Map();
    const requestDigests = new Map();
    const checks = new Map();
    const routesRoot = new URL(config.routesRoot || '/', location.origin).pathname.replace(/\/?$/, '/');
    const cartUrl = routesRoot + 'cart';
    const api = name => routesRoot + 'cart/' + name + '.js';
    const cartReadUrl = routesRoot + 'cart.js';
    const productUrl = new URL(config.url, location.origin);
    const expertUrl = new URL(config.expertUrl, location.origin);
    if (routesRoot !== '/' && !productUrl.pathname.startsWith(routesRoot)) productUrl.pathname = routesRoot + productUrl.pathname.replace(/^\//, '');
    // Reject a misconfigured template instead of falling back to ordinary tomettes.
    let invalid = productUrl.origin !== location.origin || expertUrl.origin !== location.origin;
    for (const v of config.variants || []) {
      if (!/^(hexa|carree)\|[01]$/.test(v.key) || variants.has(v.key) ||
          !/^\d+$/.test(String(v.id)) || !Number.isSafeInteger(v.priceCents) || v.priceCents <= 0) invalid = true;
      variants.set(v.key, v);
    }
    if (variants.size !== 4) invalid = true;
    function show(text, error = false) {
      status.textContent = text;
      status.dataset.error = String(error);
      if (dialog.open) expertStatus.textContent = text;
    }
    function send(type, data = {}) {
      root.dispatchEvent(new CustomEvent('mt-photo:studio',{detail:{channel: CHANNEL, type, session, ...data}}));
    }
    function progress(requestId, stage) {
      if (active?.id === requestId) active.stage = stage;
      send('cart:progress', {requestId, stage});
    }
    function choose(shape, hole) {
      const variant = variants.get(shape + '|' + (hole ? '1' : '0'));
      if (invalid || !variant || variant.available !== true) throw Error(say('Ce format est momentanément indisponible.', 'This format is temporarily unavailable.'));
      return variant;
    }
    function displayPrice(shape, hole) {
      const v = variants.get(shape + '|' + (hole ? '1' : '0'));
      price.textContent = v && v.priceCents > 0 ? new Intl.NumberFormat(config.locale, {style: 'currency', currency: config.currency}).format(v.priceCents / 100) : '';
    }
    function init(requestId) {
      send('init', {requestId, available: !invalid, product: {
        id: String(config.id), title: config.title, url: productUrl.href,
        locale: config.locale, currency: config.currency, expertUrl: expertUrl.href,
        variants: [...variants.values()], available: !invalid
      }});
    }
    // COPIÉ DANS packages/theme/photo-panier.js (svgSur, preparer, verifierLigne, verifierDisponible, journalLire) : toute retouche doit y être reportée ; svgSur y a en plus un mode souple pour les logos importés
    function svgSafe(value) {
      if (typeof value !== 'string' || bytes(value) > 4000000 || /<!DOCTYPE|<!ENTITY/i.test(value)) throw Error('SVG invalide.');
      const doc = new DOMParser().parseFromString(value, 'image/svg+xml');
      if (doc.querySelector('parsererror') || doc.documentElement.localName !== 'svg') throw Error('SVG invalide.');
      const allowed = new Set(['svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'defs', 'clipPath', 'title', 'desc']);
      for (const node of doc.querySelectorAll('*')) {
        if (!allowed.has(node.localName)) throw Error(say('Le dessin contient un élément non compatible avec la gravure.', 'The drawing contains an element that cannot be engraved.'));
        for (const a of node.attributes) {
          if (/^on/i.test(a.name) || /href$/i.test(a.name) || /(?:url\s*\((?!\s*['"]?#)|@import|expression\s*\()/i.test(a.value)) throw Error('SVG invalide.');
        }
      }
      if (!doc.querySelector('path,polygon,polyline,line,circle,ellipse,rect')) throw Error(say('Le dessin est vide.', 'The drawing is empty.'));
      return value;
    }
    // COPIÉ DANS packages/theme/photo-panier.js (svgSur, preparer, verifierLigne, verifierDisponible, journalLire) : toute retouche doit y être reportée ; svgSur y a en plus un mode souple pour les logos importés
    function prepare(input) {
      const serialized = JSON.stringify(input);
      if (!serialized || bytes(serialized) > MAX_RECIPE_BYTES) throw Error(say('Le dessin est trop volumineux pour être transmis.', 'This drawing is too large to transfer.'));
      const r = JSON.parse(serialized);
      if (!r || !['hexa', 'carree'].includes(r.shape) || typeof r.hole !== 'boolean' ||
          !Array.isArray(r.elements) || !r.elements.length || r.elements.length > 100) throw Error('Recette invalide.');
      choose(r.shape, r.hole);
      let hasDrawing = false;
      for (const e of r.elements) {
        if (!['logo', 'text'].includes(e.type) || !Number.isFinite(e.x) || !Number.isFinite(e.y) ||
            !Number.isFinite(e.size) || e.size <= 0 || e.size > 1000 || Math.abs(e.x) > 5 || Math.abs(e.y) > 5) throw Error('Placement invalide.');
        if (e.type === 'logo') {
          if (e.svg) { svgSafe(e.svg); hasDrawing = true; }
          else if (typeof e.logo !== 'string' || e.logo.length > 150) throw Error('Motif invalide.');
        } else if (typeof e.text !== 'string' || e.text.length > 500 || typeof e.font !== 'string' || e.font.length > 150) throw Error('Texte invalide.');
      }
      if (!hasDrawing) throw Error(say('La gravure de votre photo est manquante.', 'Your photo engraving is missing.'));
      // The product context is imposed after validating the customer composition.
      r.prices = Object.fromEntries([...variants].map(([key, v]) => [key, v.priceCents / 100]));
      r.allowShapes = ['hexa', 'carree']; r.allowPlein = true; r.allowPerce = true;
      r.gift = {on: false, wrap: false, msg: '', ship: false};
      delete r.fonts; // only fonts already registered by the storefront expert
      r.photoProduct = {id: String(config.id), url: productUrl.pathname};
      return r;
    }
    // COPIÉ DANS packages/theme/photo-panier.js (svgSur, preparer, verifierLigne, verifierDisponible, journalLire) : toute retouche doit y être reportée ; svgSur y a en plus un mode souple pour les logos importés
    function journalGet(id) {
      try { return JSON.parse(sessionStorage.getItem('mt-photo-cart:' + config.id + ':' + id) || 'null'); } catch { return null; }
    }
    function journalSet(id, value) {
      try { sessionStorage.setItem('mt-photo-cart:' + config.id + ':' + id, JSON.stringify(value)); } catch { /* Cart properties remain the recovery record. */ }
    }
    async function fingerprint(recipe) {
      const digest = await crypto.subtle.digest('SHA-256', encoder.encode(JSON.stringify(recipe)));
      return [...new Uint8Array(digest)].map(v => v.toString(16).padStart(2, '0')).join('');
    }
    async function fetchJSON(url, options = {}, timeout = 25000) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      try {
        const response = await fetch(url, {...options, credentials: 'same-origin', signal: controller.signal, headers: {Accept: 'application/json', ...options.headers}});
        let data;
        try { data = await response.json(); }
        catch (error) {
          // A draft's .js route can serve an HTML 404 instead of JSON.
          if (!response.ok) { const httpError = Error('HTTP ' + response.status); httpError.httpStatus = response.status; throw httpError; }
          throw error;
        }
        // Shopify errors can also arrive inside an HTTP-success envelope.
        const bodyStatus = Number(data?.status);
        if (!response.ok || bodyStatus >= 400) {
          const error = Error(data?.description || data?.message || ('HTTP ' + response.status));
          error.httpStatus = bodyStatus >= 400 ? bodyStatus : response.status; throw error;
        }
        return data;
      } finally { clearTimeout(timer); }
    }
    function getCart() { return fetchJSON(cartReadUrl + '?_photo=' + Date.now()); }
    const matchingLine = (cart, id) => cart.items?.find(item => String(item.product_id) === String(config.id) && item.properties?._photo_request === id);
    // COPIÉ DANS packages/theme/photo-panier.js (svgSur, preparer, verifierLigne, verifierDisponible, journalLire) : toute retouche doit y être reportée ; svgSur y a en plus un mode souple pour les logos importés
    function verifyLine(line, digest) {
      if (!digest || line.properties?._photo_digest !== digest) throw Error(say('Cet ajout ne correspond pas à cette version de votre dessin. Vérifiez à nouveau votre création.', 'This cart item does not match this version of your drawing. Please check your creation again.'));
      if (!line.properties['_Gravure SVG'] || !line.properties._Aperçu || !line.properties._Recette) throw Error(say('Cet ajout est incomplet. Contactez-nous avant de valider votre commande.', 'This cart item is incomplete. Please contact us before placing your order.'));
    }
    // COPIÉ DANS packages/theme/photo-panier.js (svgSur, preparer, verifierLigne, verifierDisponible, journalLire) : toute retouche doit y être reportée ; svgSur y a en plus un mode souple pour les logos importés
    async function availableVariant(variant) {
      let product;
      try {
        // Preview/theme parameters never make a draft purchasable. Check the
        // public product before loading the editor or creating any uploads.
        product = await fetchJSON(productUrl.pathname.replace(/\/$/, '') + '.js', {cache: 'no-store'});
      } catch (error) {
        if (error.httpStatus === 404 || error.httpStatus === 410) throw Error(say('Cette création n’est pas encore disponible à l’achat. Votre dessin est conservé ; réessayez un peu plus tard.', 'This creation is not available to buy yet. Your drawing is saved; please try again later.'));
        throw error;
      }
      const current = Array.isArray(product?.variants) && product.variants.find(item => String(item.id) === String(variant.id));
      if (String(product?.id) !== String(config.id) || !current || current.available !== true) throw Error(say('Ce format n’est pas disponible à l’achat pour le moment. Votre dessin est conservé ; choisissez un autre format ou réessayez plus tard.', 'This format is not available to buy at the moment. Your drawing is saved; choose another format or try again later.'));
      return variant;
    }
    function protectExpert(win, doc) {
      const style = doc.createElement('style');
      style.textContent = '#saveTop,#railSaveBtn,#saveM,#saveBtn,#siteBtn,#backBtn,#etopBack,#resetTop,.etop-back,.hdr,.pricebar,#toValid,#toValidTop,#sValid,.cart-ov,#cartM,#resumePill,.mtoolbar .mtool.cart{display:none!important}';
      doc.head.append(style);
      const prohibited = '#addCart,#another,#cartUpsell,#checkout,#applePayBtn,#shopPayBtn,#saveTop,#saveM,#saveBtn,#railSaveBtn,#siteBtn,#etopBack,#resetTop,#toValidTop,#toValid,#cartM';
      doc.addEventListener('click', event => { if (event.target.closest?.(prohibited)) { event.preventDefault(); event.stopImmediatePropagation(); } }, true);
      // Only the editor's save form may submit; native commerce remains blocked.
      doc.addEventListener('submit', event => {
        if (event.target === doc.getElementById('mtSaveForm') && event.target.matches('form#mtSaveForm')) return;
        event.preventDefault(); event.stopImmediatePropagation();
      }, true);
    }
    async function engine() {
      if (rendererTask) return rendererTask;
      rendererTask = (async () => {
        // Shopify disallows direct framing (X-Frame-Options: DENY). Reuse the
        // storefront guide's same-origin document embedding, including Safari.
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 25000);
        let html;
        try {
          const response = await fetch(expertUrl.href, {credentials: 'same-origin', signal: controller.signal, headers: {Accept: 'text/html'}});
          if (!response.ok || (response.url && new URL(response.url).origin !== location.origin)) throw Error('Configurateur inaccessible.');
          const raw = await response.text();
          if (!/<html\b/i.test(raw) || !/mt-configurateur\.js/.test(raw)) throw Error('Configurateur indisponible.');
          const base = '<base href="' + location.origin + '/">';
          html = raw.replace(/<base\b[^>]*>/gi, '').replace(/<head([^>]*)>/i, '<head$1>' + base);
        } finally { clearTimeout(timeout); }
        renderer = document.createElement('iframe');
        renderer.title = say('Configurateur expert de votre photo', 'Your photo expert editor');
        renderer.className = 'mt-photo-engine';
        renderer.setAttribute('aria-hidden', 'true'); renderer.tabIndex = -1;
        expertHost.append(renderer);
        const safari = /Safari\//.test(navigator.userAgent) && !/(?:Chrome|Chromium|CriOS|Edg|OPR)\//.test(navigator.userAgent);
        if (safari) {
          try {
            const doc = renderer.contentWindow.document;
            doc.open(); doc.write(html); doc.close();
          } catch { renderer.srcdoc = html; }
        } else renderer.srcdoc = html;
        const deadline = Date.now() + 35000;
        while (Date.now() < deadline) {
          let core;
          try { core = renderer.contentWindow.__MT_CORE__; } catch { /* Wait for same-origin document. */ }
          if (core?.loadRecipe && core.getRecipe && core.goEditor && core.renderPNG && core.buildLaserSVG) {
            protectExpert(renderer.contentWindow, renderer.contentDocument);
            return core;
          }
          await wait(100);
        }
        throw Error(say('Le moteur de gravure ne répond pas. Votre dessin est conservé ; réessayez.', 'The engraving engine did not respond. Your drawing is saved; please try again.'));
      })().catch(error => { renderer?.remove(); renderer = null; rendererTask = null; throw error; });
      return rendererTask;
    }
    function callbackExport(core, method) {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(Error(say('La préparation du fichier prend trop de temps. Réessayez sans recréer votre dessin.', 'File preparation timed out. Please retry without generating a new drawing.'))), 45000);
        try { core[method](core.state, result => { clearTimeout(timer); result ? resolve(result) : reject(Error(say('Une police ou un motif n’a pas pu être préparé. Aucun article ajouté.', 'A font or drawing could not be prepared. Nothing was added.'))); }); }
        catch (error) { clearTimeout(timer); reject(error); }
      });
    }
    function pngBlob(value) {
      if (typeof value !== 'string' || !/^data:image\/png;base64,/.test(value) || value.length > 12000000) throw Error(say('L’aperçu n’a pas pu être préparé.', 'The preview could not be prepared.'));
      const decoded = atob(value.split(',')[1]);
      if (decoded.length < 100) throw Error('Aperçu vide.');
      const data = Uint8Array.from(decoded, c => c.charCodeAt(0));
      if (data[0] !== 137 || data[1] !== 80 || data[2] !== 78 || data[3] !== 71) throw Error('Aperçu invalide.');
      return new Blob([data], {type: 'image/png'});
    }
    function nativePreviewCompatible(recipe) {
      const native = window.PhotoRenderer;
      if (typeof native?.create !== 'function') return false;
      return recipe.elements.every(e => {
        // The isolated photo painter intentionally implements the guided
        // subset. Catalogue resources and expert-only transforms stay with
        // the expert renderer rather than silently changing the cart image.
        if (e.flip || e.stroke != null) return false;
        if (e.type === 'text') return Object.hasOwn(native.fonts || {}, e.font) &&
          !/[\r\n]/.test(e.text) && !e.lines;
        if (typeof e.svg !== 'string') return false;
        const view = e.svg.match(/\bviewBox\s*=\s*["']([^"']+)["']/i);
        const box = view?.[1].trim().split(/[\s,]+/).map(Number);
        if (!box || box.length !== 4 || !box.every(Number.isFinite) || box[2] <= 0 || box[3] <= 0) return false;
        const doc = new DOMParser().parseFromString(e.svg, 'image/svg+xml');
        return [...doc.querySelectorAll('*')].every(node => [...node.attributes].every(a =>
          !['style', 'class', 'filter', 'mask'].includes(a.name)));
      });
    }
    async function renderFiles(recipe, expertPreview = false) {
      const core = await engine();
      if (core.loadRecipe(recipe) !== true) throw Error(say('Votre création n’a pas pu être chargée.', 'Your creation could not be loaded.'));
      core.render(); core.updatePrice();
      if (renderer.contentDocument.fonts) await renderer.contentDocument.fonts.ready;
      const svg = svgSafe(await callbackExport(core, 'buildLaserSVG'));
      const png = pngBlob(!expertPreview && nativePreviewCompatible(recipe)
        ? await window.PhotoRenderer.create().png(recipe, {width:1200,height:1200})
        : await callbackExport(core, 'renderPNG'));
      return {svg, png};
    }
    async function completeEdit(cart, addedLine) {
      if (!edit || edit.requestId === addedLine.properties?._photo_request) return;
      const previous = matchingLine(cart, edit.requestId);
      if (!previous) { edit = null; return; }
      // Never remove the original until all production uploads of its replacement exist.
      const after = await fetchJSON(api('change'), {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({id: previous.key, quantity: 0})});
      if (matchingLine(after, edit.requestId)) throw Error(say('La nouvelle version est au panier, mais l’ancienne n’a pas pu être retirée. Réessayez pour terminer.', 'The new version is in your cart, but the previous one could not be removed. Retry to finish.'));
      edit = null;
    }
    async function add(input, requestId, {expertPreview = false} = {}) {
      if (!validId(requestId)) throw Error('Identifiant de requête invalide.');
      progress(requestId, 'prepare');
      const recipe = prepare(input), digest = await fingerprint(recipe);
      const prior = journalGet(requestId);
      if (prior?.digest && prior.digest !== digest) throw Error(say('Votre dessin a changé. Validez à nouveau cette version.', 'Your drawing has changed. Please confirm this version again.'));
      requestDigests.set(requestId, digest);
      const before = await getCart(), found = matchingLine(before, requestId);
      if (found) {
        progress(requestId, 'verify');
        verifyLine(found, digest);
        await completeEdit(before, found);
        journalSet(requestId, {digest, state: 'complete', at: Date.now()});
        return {cartUrl};
      }
      if (prior?.state === 'complete') return {cartUrl}; // No duplicate even if the customer later removed the line.
      if (prior?.state === 'pending' && Date.now() - prior.at < 90000) throw Error(say('La réponse du panier tarde. Patientez un instant avant de vérifier à nouveau cet ajout.', 'The cart response is delayed. Please wait a moment before checking this addition again.'));
      const variant = await availableVariant(choose(recipe.shape, recipe.hole));
      const previous = edit && matchingLine(before, edit.requestId);
      const quantity = previous ? Math.max(1, Math.min(99, previous.quantity)) : 1;
      show(say('Préparation de votre aperçu et de la gravure…', 'Preparing your preview and engraving…'));
      progress(requestId, 'render');
      const {svg, png} = await renderFiles(recipe, expertPreview);
      progress(requestId, 'save');
      const modifier = new URL(productUrl); modifier.search = ''; modifier.hash = 'photo-edit=' + requestId;
      const props = {
        Texte: recipe.elements.filter(e => e.type === 'text' && e.text.trim()).map(e => e.text.trim()).join(' / ') || say('Sans texte', 'No text'),
        Forme: (recipe.shape === 'hexa' ? 'Tomette' : 'Carreau') + (recipe.hole ? say(' · avec trou', ' · with hanging hole') : say(' · sans trou', ' · without hanging hole')),
        _modifier: modifier.href, _source: 'photo', _photo_request: requestId, _photo_digest: digest
      };
      const body = new FormData(); body.append('id', String(variant.id)); body.append('quantity', String(quantity));
      for (const [key, value] of Object.entries(props)) body.append('properties[' + key + ']', value);
      body.append('properties[_Gravure SVG]', new Blob([svg], {type: 'image/svg+xml'}), 'photo-gravure.svg');
      body.append('properties[_Aperçu]', png, 'photo-apercu.png');
      body.append('properties[_Recette]', new Blob([JSON.stringify(recipe)], {type: 'application/json'}), 'photo-recette.json');
      journalSet(requestId, {digest, state: 'pending', at: Date.now()});
      show(say('Ajout de votre création au panier…', 'Adding your creation to the cart…'));
      progress(requestId, 'cart');
      try {
        await fetchJSON(api('add'), {method: 'POST', body}, 45000);
      } catch (error) {
        // A lost response is not permission to replay a non-idempotent POST.
        progress(requestId, 'verify');
        let cart; try { cart = await getCart(); } catch { /* Preserve the request for an explicit retry. */ }
        const recovered = cart && matchingLine(cart, requestId);
        if (!recovered) {
          // A confirmed validation rejection may be retried by the customer
          // after availability changes; transport failures remain ambiguous.
          if (cart && [400, 404, 410, 422].includes(error.httpStatus)) journalSet(requestId, {digest, state: 'rejected', at: Date.now()});
          error.retrySameRequest = true; throw error;
        }
        verifyLine(recovered, digest);
      }
      if (active?.stage !== 'verify') progress(requestId, 'verify');
      const after = await getCart(), line = matchingLine(after, requestId);
      if (!line) throw Error(say('Vérification du panier nécessaire. Réessayez pour vérifier cet ajout, sans recréer votre dessin.', 'The cart needs verification. Retry to check this addition without generating a new drawing.'));
      verifyLine(line, digest);
      await completeEdit(after, line);
      journalSet(requestId, {digest, state: 'complete', at: Date.now()});
      document.dispatchEvent(new CustomEvent('cart:update', {bubbles: true, detail: {cart: after, data: {itemCount: after.item_count, source: 'mytomette-photo'}}}));
      return {cartUrl};
    }
    async function checkCart(requestId) {
      const digest = requestDigests.get(requestId) || journalGet(requestId)?.digest;
      const cart = await getCart(), line = matchingLine(cart, requestId);
      if (!line) throw Error(say('L’ajout au panier n’est pas encore confirmé. Votre dessin est conservé. Réessayez pour vérifier ou terminer cet ajout.', 'The cart addition is not confirmed yet. Your drawing is saved. Please retry to check or finish this addition.'));
      verifyLine(line, digest);
      // Verification is read-only. Removing the previous version belongs to
      // the explicit add/retry flow, after its replacement is fully verified.
      if (edit && edit.requestId !== requestId && matchingLine(cart, edit.requestId)) throw Error(say('Votre nouvelle version est au panier. Réessayez pour terminer le remplacement de l’ancienne version.', 'Your new version is in the cart. Retry to finish replacing the previous version.'));
      journalSet(requestId, {digest, state: 'complete', at: Date.now()});
      return {cartUrl};
    }
    async function runCheck(requestId) {
      if (checks.has(requestId)) return checks.get(requestId);
      const promise = checkCart(requestId);
      checks.set(requestId, promise);
      try { return await promise; }
      finally { checks.delete(requestId); }
    }
    async function runAdd(recipe, requestId, options = {}) {
      if (active) {
        if (active.id === requestId) return active.promise;
        throw Error(say('Votre création est en cours d’ajout. Patientez un instant.', 'Your creation is being added. Please wait.'));
      }
      expertAdd.disabled = true;
      const operation = {id: requestId, stage: 'prepare', promise: null};
      active = operation;
      const promise = Promise.resolve().then(() => add(recipe, requestId, options));
      operation.promise = promise;
      try { return await promise; }
      finally { active = null; expertAdd.disabled = false; }
    }
    function closeExpert(approved) {
      if (active) return;
      if (dialog.open && renderer && approved !== true) { requestExpertClose(); return; }
      let saved = false, warning = '';
      if (dialog.open && expertBaseFingerprint && renderer) {
        try {
          renderer.contentDocument.activeElement?.blur();
          const core = renderer.contentWindow.__MT_CORE__;
          const snapshot = prepare(core.getRecipe(core.state));
          expertDrafts.delete(expertBaseFingerprint);
          expertDrafts.set(expertBaseFingerprint, snapshot);
          while (expertDrafts.size > 8) expertDrafts.delete(expertDrafts.keys().next().value);
          saved = true;
        } catch (error) {
          // Always let the customer leave. A valid snapshot was retained before
          // opening; never replace it with an empty/broken expert composition.
          warning = say('Cette modification n’a pas pu être enregistrée. Votre dernière version valide reste disponible dans le configurateur expert.', 'This edit could not be saved. Your last valid version remains available in the expert editor.');
        }
      }
      dialog.close(); expertHost.classList.remove('is-visible');
      dialog.setAttribute('inert', ''); dialog.setAttribute('aria-hidden', 'true');
      renderer?.setAttribute('aria-hidden', 'true'); if (renderer) renderer.tabIndex = -1;
      const message = warning || (saved ? say('Votre version expert est conservée sur cette page. Retrouvez-la dans le configurateur expert pour l’ajouter au panier.', 'Your expert version is saved on this page. Reopen the expert editor to add it to your cart.') : '');
      if (message) show(message, !!warning);
      if (expertRequest) send('expert:result', {requestId: expertRequest, ok: true, closed: true, saved, message});
      expertRequest = null; expertBaseFingerprint = null;
    }
    async function openExpert(input, requestId) {
      if (active) throw Error(say('Attendez la fin de l’ajout au panier.', 'Please wait until the cart update is complete.'));
      const guidedRecipe = prepare(input), key = await fingerprint(guidedRecipe);
      const recipe = prepare(expertDrafts.get(key) || guidedRecipe), core = await engine();
      if (core.loadRecipe(recipe) !== true) throw Error(say('Votre création n’a pas pu être chargée.', 'Your creation could not be loaded.'));
      // Retain a separate last-valid snapshot even if the customer subsequently
      // removes the photo or creates an invalid composition in the expert.
      expertDrafts.set(key, prepare(recipe));
      while (expertDrafts.size > 8) expertDrafts.delete(expertDrafts.keys().next().value);
      expertBaseFingerprint = key;
      expertRequest = requestId; expertRetryId = null; expertRetryDigest = null; expertStatus.textContent = '';
      expertHost.classList.add('is-visible'); renderer.setAttribute('aria-hidden', 'false'); renderer.tabIndex = 0;
      dialog.removeAttribute('inert'); dialog.removeAttribute('aria-hidden');
      renderer.__mtCloseAskedAt = 0;
      if (!dialog.open) dialog.showModal();
      core.render(); core.updatePrice(); core.goEditor();
      if (renderer.contentWindow.__MT_CORE__ !== core || !renderer.contentDocument.getElementById('sEdit')?.classList.contains('on')) {
        closeExpert(true);
        throw Error(say('L’éditeur n’a pas pu afficher votre création. Votre dessin est conservé ; réessayez.', 'The editor could not display your creation. Your drawing is saved; please try again.'));
      }
      prepare(core.getRecipe(core.state));
      root.querySelector('[data-photo-expert-close]').focus();
      send('expert:result', {requestId, ok: true, opened: true});
    }
    function requestExpertClose() {
      if (active || !dialog.open || !renderer) return;
      renderer.contentDocument.activeElement?.blur();
      try { if (typeof renderer.contentWindow._mtAskClose === 'function') { renderer.contentWindow._mtAskClose(); renderer.__mtCloseAskedAt = 0; return; } } catch {}
      const now = Date.now();
      if (renderer.__mtCloseAskedAt && now - renderer.__mtCloseAskedAt >= 2000) {
        renderer.__mtCloseAskedAt = 0;
        if (window.confirm(say('L’éditeur ne répond pas. Quitter sans enregistrer ? Votre création risque d’être perdue.', 'The editor is not responding. Leave without saving? Your design may be lost.'))) closeExpert(true);
        return;
      }
      if (!renderer.__mtCloseAskedAt) renderer.__mtCloseAskedAt = now;
      renderer.contentWindow.postMessage({mtcfg: 'requestClose'}, '*');
    }
    window.addEventListener('message', event => {
      if (!dialog.open || !renderer || event.source !== renderer.contentWindow) return;
      if (event.data?.mtcfg === 'close') closeExpert(true);
    });
    root.querySelector('[data-photo-expert-close]').addEventListener('click', requestExpertClose);
    dialog.addEventListener('cancel', event => { event.preventDefault(); requestExpertClose(); });
    expertAdd.addEventListener('click', async () => {
      try {
        const core = await engine();
        // Commit an inline text edit before taking the expert's composition snapshot.
        renderer.contentDocument.activeElement?.blur();
        const recipe = prepare(core.getRecipe(core.state));
        const digest = await fingerprint(recipe);
        if (expertRetryDigest !== digest) { expertRetryId = uuid(); expertRetryDigest = digest; }
        expertRetryId ||= uuid();
        const result = await runAdd(recipe, expertRetryId, {expertPreview: true});
        location.assign(result.cartUrl);
      } catch (error) { show(error.message, true); }
    });
    async function restoreCartDesign() {
      if (restored) return; restored = true;
      const hash = new URLSearchParams(location.hash.slice(1));
      const resume = hash.get('photo-resume');
      if (resume !== null) {
        try {
          if (!/^[A-Za-z0-9_-]+$/.test(resume) || resume.length > Math.ceil(MAX_RECIPE_BYTES * 4 / 3) + 4) throw Error('Recette invalide.');
          const binary = atob(resume.replace(/-/g, '+').replace(/_/g, '/'));
          const raw = new TextDecoder('utf-8', {fatal: true}).decode(Uint8Array.from(binary, char => char.charCodeAt(0)));
          if (bytes(raw) > MAX_RECIPE_BYTES) throw Error('Recette trop volumineuse.');
          const recipe = JSON.parse(raw), context = recipe?.photoProduct;
          if (!context || String(context.id) !== String(config.id) || typeof context.url !== 'string') throw Error('Produit de la création invalide.');
          const route = new URL(context.url, location.origin);
          const productPath = url => url.pathname.replace(/^\/(?:en|fr)(?=\/)/, '').replace(/\/$/, '');
          if (route.origin !== location.origin || route.search || route.hash || productPath(route) !== productPath(productUrl)) throw Error('Produit de la création invalide.');
          // The saved recipe supplies the drawing only. This Liquid product's
          // variants/prices replace its commerce fields inside prepare().
          await openExpert(prepare(recipe), uuid());
        } catch (error) { show(say('Cette création photo ne peut pas être rouverte. Votre lien est conservé ; contactez-nous pour la récupérer.', 'This photo creation could not be reopened. Your link is preserved; contact us to recover it.'), true); }
        return;
      }
      const requestId = hash.get('photo-edit');
      if (!validId(requestId)) return;
      try {
        const cart = await getCart(), item = matchingLine(cart, requestId);
        if (!item) throw Error(say('Cette création n’est plus dans votre panier.', 'This creation is no longer in your cart.'));
        const url = new URL(item.properties._Recette, location.origin);
        const trustedHost = url.origin === location.origin || url.hostname === 'cdn.shopify.com';
        if (!trustedHost || url.protocol !== 'https:' || !url.pathname.includes('/uploads/')) throw Error('Lien de recette invalide.');
        const response = await fetch(url.href, {credentials: 'omit'});
        if (!response.ok) throw Error('Recette inaccessible.');
        const raw = await response.text(); if (bytes(raw) > MAX_RECIPE_BYTES) throw Error('Recette trop volumineuse.');
        edit = {requestId, key: item.key};
        await openExpert(JSON.parse(raw), uuid());
      } catch (error) { show(error.message, true); }
    }
    root.addEventListener('mt-photo:commerce', async event => {
      const message = event.detail;
      if (!message || typeof message !== 'object' || message.channel !== CHANNEL || typeof message.type !== 'string') return;
      if (message.type === 'ready') {
        ready = true; init(message.requestId); studio.removeAttribute('aria-busy');
        show(invalid ? say('Cette création sera bientôt disponible.', 'This creation will be available soon.') : '');
        if (!invalid) restoreCartDesign(); return;
      }
      if (message.session !== session) return;
      if (message.type === 'initialized') {
        ready = true; studio.removeAttribute('aria-busy');
        show(invalid ? say('Cette création sera bientôt disponible.', 'This creation will be available soon.') : '');
        if (!invalid) restoreCartDesign();
      }
      if (message.type === 'resize' || message.type === 'initialized') {
        const height = Number(message.height);
        // Native page: normal document flow owns height.
        return;
      }
      if (message.type === 'state:change') { if (['hexa', 'carree'].includes(message.shape) && typeof message.hole === 'boolean') displayPrice(message.shape, message.hole); return; }
      if (!validId(message.requestId)) return;
      if (message.type === 'expert:open') {
        try { await openExpert(message.recipe, message.requestId); }
        catch (error) { send('expert:result', {requestId: message.requestId, ok: false, error: error.message}); show(error.message, true); }
      } else if (message.type === 'cart:add') {
        try {
          const result = complete.get(message.requestId) || await runAdd(message.recipe, message.requestId);
          complete.set(message.requestId, result);
          show(say('Votre création est dans le panier.', 'Your creation is in your cart.'));
          send('cart:result', {requestId: message.requestId, ok: true, ...result});
          // Only the Shopify parent navigates; the customer iframe stays sandboxed.
          location.assign(result.cartUrl);
        } catch (error) {
          show(error.message, true);
          send('cart:result', {requestId: message.requestId, ok: false, error: error.message, retrySameRequest: true});
        }
      } else if (message.type === 'cart:check') {
        if (active?.id === message.requestId) {
          progress(message.requestId, active.stage);
          return;
        }
        progress(message.requestId, 'verify');
        try {
          const result = await runCheck(message.requestId);
          complete.set(message.requestId, result);
          show(say('Votre création est dans le panier.', 'Your creation is in your cart.'));
          send('cart:result', {requestId: message.requestId, ok: true, ...result});
          if (!active || active.id === message.requestId) location.assign(result.cartUrl);
        } catch (error) {
          show(error.message, true);
          send('cart:result', {requestId: message.requestId, ok: false, error: error.message, retrySameRequest: true});
        }
      }
    });
    
    init();
    setTimeout(() => { if (!ready) show(say('Le studio prend un peu de temps à charger. Votre panier reste disponible.', 'The studio is taking a little longer to load. Your cart remains available.')); }, 20000);
    if (invalid) show(say('Cette création sera bientôt disponible.', 'This creation will be available soon.'));
  }
  document.querySelectorAll('[data-photo-product]').forEach(mount);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-photo-product]').forEach(mount));
})();
